/**
 * LazyImage Component
 * 
 * Implements lazy loading for images to improve performance
 * Only loads images when they enter the viewport
 */

import React, { useState, useRef, useEffect } from 'react';

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  placeholder?: string;
  className?: string;
  onError?: () => void;
  onLoad?: () => void;
  showPlaceholder?: boolean;
  priority?: boolean;
  blurDataURL?: string; // Base64 blur placeholder
  srcSet?: string;
  sizes?: string;
  fetchPriority?: 'high' | 'low' | 'auto';
}

const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  placeholder = 'https://via.placeholder.com/64x64?text=Loading',
  className = '',
  onError,
  onLoad,
  showPlaceholder = true,
  priority = false,
  blurDataURL,
  srcSet: providedSrcSet,
  sizes: providedSizes,
  fetchPriority = priority ? 'high' : 'auto',
  width,
  height,
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState<string>(blurDataURL || placeholder);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Generate responsive srcSet if not provided
  const generateSrcSet = (imageSrc: string): string => {
    if (providedSrcSet) return providedSrcSet;
    
    // Generate multiple sizes for responsive images
    const sizeOptions = [320, 480, 640, 960, 1280, 1920];
    return sizeOptions
      .map(size => `${imageSrc} ${size}w`)
      .join(', ');
  };

  // Generate sizes attribute if not provided
  const generateSizes = (): string => {
    if (providedSizes) return providedSizes;
    
    // Default responsive sizes
    return '(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw';
  };

  useEffect(() => {
    // If priority is true or IntersectionObserver is not supported, load immediately
    if (priority || !('IntersectionObserver' in window)) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before image enters viewport
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [priority]);

  useEffect(() => {
    if (isInView && src && src !== placeholder && src !== blurDataURL) {
      // If we have blur data, show it first, then load full image
      if (blurDataURL && imageSrc === blurDataURL) {
        // Small delay to show blur effect
        const timer = setTimeout(() => {
          setImageSrc(src);
        }, 50);
        return () => clearTimeout(timer);
      } else {
        setImageSrc(src);
      }
    }
  }, [isInView, src, placeholder, blurDataURL, imageSrc]);

  const handleLoad = () => {
    setIsLoaded(true);
    if (onLoad) {
      onLoad();
    }
  };

  const handleError = () => {
    setImageSrc(placeholder);
    if (onError) {
      onError();
    }
  };

  return (
    <img
      ref={imgRef}
      src={imageSrc}
      srcSet={imageSrc === src ? generateSrcSet(src) : undefined}
      sizes={imageSrc === src ? generateSizes() : undefined}
      alt={alt}
      width={width}
      height={height}
      className={`${className} ${!isLoaded ? 'opacity-0' : blurDataURL && imageSrc === blurDataURL ? 'opacity-50 blur-sm' : 'opacity-100'} transition-opacity duration-300`}
      style={{
        imageRendering: 'auto',
        ...props.style
      }}
      onLoad={handleLoad}
      onError={handleError}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={fetchPriority}
      {...props}
    />
  );
};

export default LazyImage;
